"use client";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shadcn/ui/dialog";
import { useCallback, useEffect, useMemo, useState } from "react";

import { Input } from "@/shadcn/ui/input";
import useIsSuperAdmin from "@/hooks/useIsSuperAdmin";
import { useRouter } from "next/navigation";
import { useGate } from "statsig-react";
import { SUPPORT_ASSISTANT_NAME } from "@punchup/support/vocabulary";

export interface Command {
  id: string;
  label: string;
  description?: string;
  action: () => void;
  keywords?: string[];
}

interface CommandPaletteProps {
  commands?: Command[];
}

const defaultCommands = (
  router: ReturnType<typeof useRouter>,
  isSuperAdmin: boolean,
  subscriptionsNavEnabled: boolean,
  supportAssistantEnabled: boolean
): Command[] => {
  const baseCommands = [
    {
      id: "home",
      label: "Home Feed",
      description: "View the feed home page",
      action: () => router.push("/"),
      keywords: ["home", "main", "index", "feed"],
    },
    {
      id: "shows",
      label: "Nearby Shows",
      description: "View upcoming comedy shows near you",
      action: () => router.push("/nearby"),
      keywords: ["shows", "events", "tickets", "nearby"],
    },
    {
      id: "library",
      label: subscriptionsNavEnabled ? "Subscriptions" : "Library",
      description: subscriptionsNavEnabled
        ? "Browse subscriptions"
        : "Discover comedians",
      action: () => router.push("/library"),
      keywords: subscriptionsNavEnabled
        ? ["subscriptions", "subscribe", "library", "comedians", "comics"]
        : ["comedians", "comics", "library"],
    },
    {
      id: "search",
      label: "Search",
      description: "Open the search page",
      action: () => router.push("/search"),
      keywords: ["search"],
    },
    {
      id: "merch",
      label: "Merch",
      description: "Browse the merch store",
      action: () => router.push("/merch"),
      keywords: ["merch", "store", "products"],
    },
    {
      id: "notifications",
      label: "Notifications",
      description: "View your notifications",
      action: () => router.push("/notifications"),
      keywords: ["notifications", "alerts", "updates"],
    },
    {
      id: "profile",
      label: "Profile",
      description: "View your profile",
      action: () => router.push("/notifications"),
      keywords: ["profile", "account", "settings", "preferences"],
    },
  ];

  if (isSuperAdmin) {
    baseCommands.push({
      id: "internal",
      label: "Internal",
      description: "Internal Tools",
      action: () => router.push("/internal"),
      keywords: ["internal"],
    });
    baseCommands.push({
      id: "internal-users",
      label: "Internal - Users",
      description: "[internal] Users",
      action: () => router.push("/internal/users"),
      keywords: ["internal", "users"],
    });
    baseCommands.push({
      id: "internal-payments",
      label: "Internal - Payments Dashboard",
      description: "[internal] Payments dashboard",
      action: () => router.push("/internal/payments"),
      keywords: ["internal", "payments", "dashboard"],
    });
    baseCommands.push({
      id: "internal-comedians",
      label: "Internal - Comedians",
      description: "[internal] Comedians",
      action: () => router.push("/internal/comedians"),
      keywords: ["internal", "comedians"],
    });
    baseCommands.push({
      id: "internal-api-test",
      label: "Internal - API Test Tool",
      description: "[internal] API Test",
      action: () => router.push("/internal/devs/api-test"),
      keywords: ["internal", "api", "dev"],
    });
    baseCommands.push({
      id: "internal-feedback",
      label: "Internal - App Feedback",
      description: "[internal] AppFeedback",
      action: () => router.push("/internal/feedback"),
      keywords: ["internal", "app", "feedback"],
    });
    if (supportAssistantEnabled) {
      baseCommands.push({
        id: "internal-support-assistant",
        label: `Internal - Ask ${SUPPORT_ASSISTANT_NAME} (support assistant)`,
        description: `[internal] Ask ${SUPPORT_ASSISTANT_NAME} how to fix a support case`,
        action: () => router.push("/internal/support"),
        keywords: [
          "internal",
          "support",
          "help",
          "assistant",
          "chat",
          "ask",
          "maxnu",
        ],
      });
      baseCommands.push({
        id: "internal-support-cases",
        label: "Internal - Support Knowledge Base",
        description: "[internal] Solved support cases",
        action: () => router.push("/internal/support/cases"),
        keywords: ["internal", "support", "help", "cases", "knowledge", "kb"],
      });
    }
  }
  return baseCommands;
};

export function CommandPalette({ commands }: CommandPaletteProps) {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const isSuperAdmin = useIsSuperAdmin();
  const { value: subscriptionsNavEnabled } = useGate("subscriptions_nav_bar");
  const { value: supportAssistantEnabled } = useGate("support_assistant");

  const allCommands = useMemo(
    () =>
      commands ||
      defaultCommands(
        router,
        !!isSuperAdmin,
        subscriptionsNavEnabled,
        supportAssistantEnabled
      ),
    [
      commands,
      router,
      isSuperAdmin,
      subscriptionsNavEnabled,
      supportAssistantEnabled,
    ]
  );

  const filteredCommands = useMemo(() => {
    if (!search) return allCommands;

    const searchLower = search.toLowerCase();
    return allCommands.filter((cmd) => {
      const labelMatch = cmd.label.toLowerCase().includes(searchLower);
      const descMatch = cmd.description?.toLowerCase().includes(searchLower);
      const keywordMatch = cmd.keywords?.some((kw) =>
        kw.toLowerCase().includes(searchLower)
      );
      return labelMatch || descMatch || keywordMatch;
    });
  }, [search, allCommands]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Cmd+K on Mac, Ctrl+K on Windows/Linux
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setIsOpen((prev) => !prev);
        return;
      }

      if (!isOpen) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < filteredCommands.length - 1 ? prev + 1 : prev
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case "Enter":
          e.preventDefault();
          if (filteredCommands[selectedIndex]) {
            filteredCommands[selectedIndex].action();
            setIsOpen(false);
            setSearch("");
            setSelectedIndex(0);
          }
          break;
        case "Escape":
          e.preventDefault();
          setIsOpen(false);
          setSearch("");
          setSelectedIndex(0);
          break;
      }
    },
    [isOpen, filteredCommands, selectedIndex]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    // Reset selected index when filtered commands change
    setSelectedIndex(0);
  }, [filteredCommands]);

  const handleClose = () => {
    setIsOpen(false);
    setSearch("");
    setSelectedIndex(0);
  };

  const handleCommandClick = (command: Command) => {
    command.action();
    handleClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-2xl p-0 gap-0">
        <DialogHeader className="px-4 pt-4 pb-2 border-b">
          <DialogTitle className="text-sm font-normal text-muted-foreground">
            Command Palette
          </DialogTitle>
        </DialogHeader>
        <div className="p-2">
          <Input
            autoFocus
            placeholder="Type a command or search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </div>
        <div className="max-h-[400px] overflow-y-auto">
          {filteredCommands.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              No commands found
            </div>
          ) : (
            <div className="py-2">
              {filteredCommands.map((command, index) => (
                <button
                  key={command.id}
                  onClick={() => handleCommandClick(command)}
                  className={`w-full px-4 py-3 text-left transition-colors ${
                    index === selectedIndex
                      ? "bg-neutral-100 dark:bg-neutral-800"
                      : "hover:bg-neutral-50 dark:hover:bg-neutral-900"
                  }`}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div className="font-medium text-sm">{command.label}</div>
                  {command.description && (
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {command.description}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="px-4 py-2 border-t bg-neutral-50 dark:bg-neutral-900 text-xs text-muted-foreground">
          Press{" "}
          <kbd className="px-1.5 py-0.5 rounded bg-white dark:bg-neutral-800 border shadow-sm">
            ⌘K
          </kbd>{" "}
          or{" "}
          <kbd className="px-1.5 py-0.5 rounded bg-white dark:bg-neutral-800 border shadow-sm">
            Ctrl+K
          </kbd>{" "}
          to toggle • Use ↑↓ to navigate • Enter to select • Esc to close
        </div>
      </DialogContent>
    </Dialog>
  );
}
